import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import CoachRulesModal from "./CoachRulesModal";
import { BACKEND_URL } from "../config";

function fmtList(x) {
  if (!x) return "—";
  if (Array.isArray(x)) return x.filter(Boolean).join(", ") || "—";
  const s = String(x).trim();
  return s ? s : "—";
}

function clampText(s, n = 90) {
  const t = (s ?? "").toString().trim();
  if (!t) return "—";
  if (t.length <= n) return t;
  return t.slice(0, n - 1) + "…";
}

function YesNo(v) {
  const s = (v ?? "").toString().toLowerCase();
  if (s === "yes" || s === "true") return "Yes";
  if (s === "no" || s === "false") return "No";
  return v ? String(v) : "—";
}

function isBlank(x) {
  if (x == null) return true;
  if (typeof x === "string") return x.trim() === "";
  return false;
}

function isAnswered(v) {
  if (v == null) return false;
  if (Array.isArray(v)) return v.filter((x) => x !== null && x !== undefined && String(x).trim() !== "").length > 0;
  if (typeof v === "boolean") return true; // yes/no answered either way
  if (typeof v === "number") return Number.isFinite(v);
  if (typeof v === "string") return v.trim() !== "";
  if (typeof v === "object") {
    const name = v.name || v.filename || v.file_name || v.originalname;
    return name ? String(name).trim() !== "" : false;
  }
  return false;
}

function isQuestionnaireComplete(q) {
  if (!q || typeof q !== "object") return false;

  // Bloodwork file is optional; all other questions should be answered.
  const requiredKeys = [
    "steps_range",
    "current_exercise",
    "gym_access",
    "gym_experience",
    "workouts_love",
    "workouts_days_per_week",
    "health_conditions",
    "current_injuries",
    "past_injuries",
    "pain_notes",
    "active_hobbies",
    "eating_habits",
    "foods_love",
    "foods_hate_allergies",
    "fruits_veggies",
    "supplements",
    "yo_yo_diet",
    "tracked_macros_before",
    "smart_watch",
    "activity_level_label",
    "preferred_workout_days",
    "dietary_restrictions",
    "allergies",
    "dislikes",
    "medical_constraints",
    "injury_constraints",
    "equipment_available",
    "forbidden_topics",
  ];
  // "Other" free-text is optional and should not block questionnaire completion.

  for (const k of requiredKeys) {
    if (!Object.prototype.hasOwnProperty.call(q, k)) return false;
    if (!isAnswered(q[k])) return false;
  }

  return true;
}

function isCoachRulesEmpty(rules) {
  const r = rules && typeof rules === "object" ? rules : {};

  const listFields = [
    "dietary_restrictions",
    "allergies",
    "dislikes",
    "injury_constraints",
    "forbidden_topics",
  ];

  for (const k of listFields) {
    const v = r[k];
    if (Array.isArray(v) && v.filter(Boolean).length > 0) return false;
  }

  if (!isBlank(r.notes_for_coach)) return false;

  const tone = String(r.tone || "").trim().toLowerCase();
  if (tone && tone !== "encouraging") return false;

  return true;
}

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function lbsToKg(lbs) {
  return lbs * 0.45359237;
}

function feetInchesToCm(feet, inches) {
  return ((Number(feet || 0) * 12) + Number(inches || 0)) * 2.54;
}

function activityMultiplier(level) {
  const s = String(level || "").toLowerCase();
  if (s.includes("sedentary")) return 1.2;
  if (s.includes("light")) return 1.375;
  if (s.includes("moderate")) return 1.55;
  if (s.includes("very")) return 1.725;
  if (s.includes("extra")) return 1.9;
  return 1.375;
}

function computeBmr({ weight, height_feet, height_inches, age, gender }) {
  const wLbs = toNumber(weight);
  const a = toNumber(age);
  const hCm = feetInchesToCm(height_feet, height_inches);

  if (!wLbs || !a || !hCm) return null;

  const wKg = lbsToKg(wLbs);
  const isMale = String(gender || "").toLowerCase() === "male";

  // Mifflin-St Jeor
  return Math.round((10 * wKg) + (6.25 * hCm) - (5 * a) + (isMale ? 5 : -161));
}

function isReasonableCalories(n) {
  return Number.isFinite(n) && n >= 800 && n <= 8000;
}

export default function ProfileView({ profile, onEditProfile, token, authUser, showToast }) {
// Batch 7: Avatar + Badges + Connected apps
const fileRef = useRef(null);
const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || profile?.profile_picture_url || null);
const [badges, setBadges] = useState([]);
const [badgesLoading, setBadgesLoading] = useState(false);

useEffect(() => {
  setAvatarUrl(profile?.avatar_url || profile?.profile_picture_url || null);
}, [profile]);

const loadBadges = async () => {
  if (!token) return;
  setBadgesLoading(true);
  try {
    const res = await fetch(`${BACKEND_URL}/api/badges/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      setBadges([]);
      return;
    }
    const data = await res.json();
    setBadges(data.badges || data.items || []);
  } catch (e) {
    console.warn("Load badges failed:", e?.message || e);
    setBadges([]);
  } finally {
    setBadgesLoading(false);
  }
};

useEffect(() => {
  loadBadges();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [token]);

const uploadAvatar = async (file) => {
  if (!file || !token) return;
  try {
    const fd = new FormData();
    fd.append("file", file);

    const res = await fetch(`${BACKEND_URL}/api/profile/avatar`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });

    if (!res.ok) {
      // Back-compat: if endpoint not present yet
      const txt = await res.text();
      showToast?.("Avatar upload not available yet: " + (txt || `HTTP ${res.status}`), "info");
      return;
    }

    const out = await res.json();
    const url = out.avatar_url || out.url || out.profile_picture_url;
    if (url) setAvatarUrl(url);
    showToast?.("✅ Profile picture updated!");
  } catch (e) {
    showToast?.("Avatar upload failed: " + (e?.message || String(e)), "error");
  }
};

const connectedApps = useMemo(() => {
  // Try from profile object first
  const c = profile?.connected_apps || profile?.devices || profile?.wearables || null;
  if (Array.isArray(c)) return c;

  // Otherwise infer from any provider flags we might have
  const list = [];
  const providers = profile?.providers || profile?.connected_providers || {};
  if (providers?.fitbit) list.push({ name: "Fitbit", status: "connected" });
  if (providers?.oura) list.push({ name: "Oura", status: "connected" });
  if (providers?.withings) list.push({ name: "Withings", status: "connected" });
  if (providers?.health_connect) list.push({ name: "Health Connect", status: "connected" });
  if (providers?.healthkit) list.push({ name: "Apple Health", status: "connected" });
  return list;
}, [profile]);

  const [showCoachRules, setShowCoachRules] = useState(false);
  const [themeSaving, setThemeSaving] = useState(false);
  const userId = authUser?.user_id || "";
  const canSaveTheme = !!token && !!userId;

  const questionnaireFilled = useMemo(() => {
    const q = (profile?.questionnaire && typeof profile.questionnaire === "object")
      ? profile.questionnaire
      : {};
    const mergedQ = { ...q };

    if (!Object.prototype.hasOwnProperty.call(mergedQ, "activity_level_label") && profile?.activity_level_label != null) {
      mergedQ.activity_level_label = profile.activity_level_label;
    }
    if (!Object.prototype.hasOwnProperty.call(mergedQ, "preferred_workout_days") && profile?.preferred_workout_days != null) {
      mergedQ.preferred_workout_days = profile.preferred_workout_days;
    }

    return isQuestionnaireComplete(mergedQ);
  }, [profile]);

  const currentTheme = useMemo(() => {
    const htmlTheme = document.documentElement.getAttribute("data-theme");
    if (htmlTheme === "dark" || htmlTheme === "light") return htmlTheme;
    const p = (profile?.theme || "").toLowerCase();
    if (p === "dark" || p === "light") return p;
    return "light";
  }, [profile?.theme]);

  useEffect(() => {
    if (!profile) return;
    const p = (profile?.theme || "").toLowerCase();
    if (p === "dark" || p === "light") {
      document.documentElement.setAttribute("data-theme", p);
    }
  }, [profile]);

    const fallbackBmr = useMemo(() => {
    return computeBmr({
      weight: profile?.weight,
      height_feet: profile?.height_feet,
      height_inches: profile?.height_inches,
      age: profile?.age,
      gender: profile?.gender,
    });
  }, [profile]);

  const safeBmr = useMemo(() => {
    const raw = toNumber(profile?.bmr);
    return isReasonableCalories(raw) ? Math.round(raw) : fallbackBmr;
  }, [profile?.bmr, fallbackBmr]);

  const safeTdee = useMemo(() => {
    const raw = toNumber(profile?.tdee);
    if (isReasonableCalories(raw)) return Math.round(raw);
    if (!safeBmr) return null;
    return Math.round(safeBmr * activityMultiplier(profile?.activity_level));
  }, [profile?.tdee, profile?.activity_level, safeBmr]);

  const safeDailyTarget = useMemo(() => {
    const raw = toNumber(profile?.daily_target);
    if (isReasonableCalories(raw)) return Math.round(raw);
    if (!safeTdee) return null;

    const currentWeight = toNumber(profile?.weight);
    const goalWeight = toNumber(profile?.goal_weight);

    if (currentWeight && goalWeight) {
      if (goalWeight < currentWeight) return Math.max(1200, safeTdee - 500);
      if (goalWeight > currentWeight) return safeTdee + 250;
    }
    return safeTdee;
  }, [profile?.daily_target, profile?.weight, profile?.goal_weight, safeTdee]);

  const toggleTheme = useCallback(async () => {
    const prev = currentTheme;
    const next = prev === "dark" ? "light" : "dark";

    document.documentElement.setAttribute("data-theme", next);

    if (!canSaveTheme) return;

    setThemeSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${encodeURIComponent(userId)}/preferences`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ theme: next }),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to save theme");
      }
    } catch (e) {
      document.documentElement.setAttribute("data-theme", prev);
      showToast?.("Error saving theme: " + (e?.message || String(e)), "error");
    } finally {
      setThemeSaving(false);
    }
  }, [currentTheme, canSaveTheme, token, userId, showToast]);

  if (!profile) {
    return (
      <div className="profile-view">

{/* Batch 7: Profile header with avatar */}
<div className="card" style={{ padding: 16, marginBottom: 16 }}>
  <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
    <div style={{ width: 84, height: 84, borderRadius: 18, overflow: "hidden", border: "1px solid var(--border-light)", background: "var(--bg-section)", display: "grid", placeItems: "center" }}>
      {avatarUrl ? (
        <img src={avatarUrl} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <div style={{ fontSize: 34 }}>👤</div>
      )}
    </div>

    <div style={{ minWidth: 240 }}>
      <div className="heading-4" style={{ marginBottom: 4 }}>{profile?.name || authUser?.name || "Profile"}</div>
      <div className="body-small" style={{ color: "var(--text-muted)" }}>
        {authUser?.email || ""}
      </div>
    </div>

    <div style={{ marginLeft: "auto", display: "flex", gap: 10, flexWrap: "wrap" }}>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) uploadAvatar(f);
          e.target.value = "";
        }}
      />
      <button type="button" className="btn-secondary" onClick={() => fileRef.current?.click()}>
        Upload PFP
      </button>
      <button type="button" className="btn-primary" onClick={onEditProfile}>
        Edit Profile
      </button>
    </div>
  </div>
</div>

{/* Batch 7: Badges */}
<div className="card" style={{ padding: 16, marginBottom: 16 }}>
  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
    <div>
      <div className="heading-4" style={{ marginBottom: 4 }}>Badges</div>
      <div className="body-small" style={{ color: "var(--text-muted)" }}>
        Streaks, milestones, goals completed, and personal bests.
      </div>
    </div>
    <button type="button" className="btn-secondary" onClick={loadBadges} disabled={badgesLoading} style={{ padding: "8px 12px" }}>
      {badgesLoading ? "…" : "Refresh"}
    </button>
  </div>

  {badges?.length ? (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 12 }}>
      {badges.slice(0, 24).map((b, idx) => (
        <div key={b.badge_id || idx} style={{ border: "1px solid var(--border-light)", borderRadius: 14, padding: 12, background: "var(--bg-section)" }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>{b.icon || "🏅"}</div>
          <div style={{ fontWeight: 900 }}>{b.title || b.name || "Badge"}</div>
          <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 6 }}>
            {b.description || ""}
          </div>
          {b.earned_at ? (
            <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 8 }}>
              Earned: {String(b.earned_at).slice(0, 10)}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  ) : (
    <div className="body-medium" style={{ color: "var(--text-secondary)", marginTop: 12 }}>
      No badges yet — complete habits and goals to earn them.
    </div>
  )}
</div>

{/* Batch 7: Connected Apps */}
<div className="card" style={{ padding: 16, marginBottom: 16 }}>
  <div className="heading-4" style={{ marginBottom: 6 }}>Connected Apps</div>
  <div className="body-small" style={{ color: "var(--text-muted)" }}>
    Fitbit, Oura, Withings, Health Connect, and more.
  </div>

  <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
    {connectedApps?.length ? (
      connectedApps.map((a, idx) => (
        <div key={idx} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: 12, borderRadius: 12, border: "1px solid var(--border-light)", background: "var(--bg-section)" }}>
          <div style={{ fontWeight: 900 }}>{a.name || a.provider || "Device"}</div>
          <div className="body-small" style={{ color: a.status === "connected" ? "#10b981" : "var(--text-muted)", fontWeight: 800 }}>
            {a.status || "connected"}
          </div>
        </div>
      ))
    ) : (
      <div className="body-medium" style={{ color: "var(--text-secondary)" }}>
        No connected apps detected. Visit “Connect Devices” in Settings.
      </div>
    )}
  </div>
</div>        <h1 className="heading-2">Profile</h1>
        <p className="body-large" style={{ color: "var(--text-muted)", marginTop: "32px", textAlign: "center" }}>
          No profile found. Please set up your profile.
        </p>
      </div>
    );
  }

  const q = (profile?.questionnaire && typeof profile.questionnaire === "object") ? profile.questionnaire : {};
  const bloodworkName = q?.bloodwork_file?.name || q?.bloodwork_file?.filename || null;

  return (
    <div className="profile-view">
      <h1 className="heading-2">Profile</h1>

      {/* CTA: questionnaire incomplete */}
      {!questionnaireFilled && (
        <div
          style={{
            marginTop: 14,
            marginBottom: 14,
            padding: 14,
            borderRadius: 14,
            border: "1px solid rgba(0, 128, 255, 0.25)",
            background: "rgba(0, 128, 255, 0.06)",
            display: "grid",
            gap: 10,
          }}
        >
          <div style={{ fontWeight: 900 }}>
            📝 Fill out your questionnaire to improve suggestions
          </div>
          <div className="body-small" style={{ color: "var(--text-secondary)" }}>
            The AI coach can make better workout, habit, and nutrition suggestions when it knows your preferences, injuries, and routine.
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn-primary"
              onClick={onEditProfile}
            >
              Fill Out Questionnaire
            </button>
          </div>
        </div>
      )}

      <div className="profile-info">
        <div className="info-row">
          <span className="info-label">Name:</span>
          <span className="info-value">{profile.name}</span>
        </div>
        <div className="info-row">
          <span className="info-label">Age:</span>
          <span className="info-value">{profile.age} years</span>
        </div>
        <div className="info-row">
          <span className="info-label">Gender:</span>
          <span className="info-value" style={{ textTransform: "capitalize" }}>
            {profile.gender}
          </span>
        </div>
        <div className="info-row">
          <span className="info-label">Height:</span>
          <span className="info-value">
            {profile.height_feet}' {profile.height_inches}"
          </span>
        </div>
        <div className="info-row">
          <span className="info-label">Current Weight:</span>
          <span className="info-value">{profile.weight} lbs</span>
        </div>
        <div className="info-row">
          <span className="info-label">Goal Weight:</span>
          <span className="info-value">{profile.goal_weight} lbs</span>
        </div>
        <div className="info-row">
          <span className="info-label">Activity Level:</span>
          <span className="info-value" style={{ textTransform: "capitalize" }}>
            {(profile.activity_level || "").replace("_", " ")}
          </span>
        </div>

        <div className="divider"></div>

        <div className="info-row highlight">
          <span className="info-label">BMR:</span>
          <span className="info-value">{safeBmr ?? "—"} cal/day</span>
        </div>
        <div className="info-row highlight">
          <span className="info-label">TDEE:</span>
          <span className="info-value">{safeTdee ?? "—"} cal/day</span>
        </div>
        <div className="info-row highlight">
          <span className="info-label">Daily Target:</span>
          <span className="info-value">{safeDailyTarget ?? "—"} cal/day</span>
        </div>

        {/* Questionnaire (compact + expandable) */}
        <div className="divider"></div>

        <details style={{ marginTop: 12 }}>
          <summary className="body-medium" style={{ cursor: "pointer", fontWeight: 800 }}>
            Questionnaire (tap to expand)
          </summary>

          {/* Compact summary */}
          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            <div className="info-row">
              <span className="info-label">Steps:</span>
              <span className="info-value">{fmtList(q.steps_range)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Workouts/wk:</span>
              <span className="info-value">{fmtList(q.workouts_days_per_week)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Preferred days:</span>
              <span className="info-value">{fmtList(q.preferred_workout_days)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Workouts you love:</span>
              <span className="info-value">
                {fmtList(q.workouts_love)}{q.workouts_love_other ? ` (${q.workouts_love_other})` : ""}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Tracked macros before:</span>
              <span className="info-value">{YesNo(q.tracked_macros_before)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Smart watch:</span>
              <span className="info-value">{YesNo(q.smart_watch)}</span>
            </div>
            {bloodworkName && (
              <div className="info-row">
                <span className="info-label">Bloodwork file:</span>
                <span className="info-value">{bloodworkName}</span>
              </div>
            )}

            {/* Expanded long answers */}
            <div className="divider"></div>

            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Current exercise:</span>
              <span className="info-value" title={q.current_exercise || ""}>{clampText(q.current_exercise, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Gym access/equipment:</span>
              <span className="info-value" title={q.gym_access || ""}>{clampText(q.gym_access, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Gym experience:</span>
              <span className="info-value" title={q.gym_experience || ""}>{clampText(q.gym_experience, 220)}</span>
            </div>

            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Health conditions:</span>
              <span className="info-value" title={q.health_conditions || ""}>{clampText(q.health_conditions, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Current injuries:</span>
              <span className="info-value" title={q.current_injuries || ""}>{clampText(q.current_injuries, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Past injuries:</span>
              <span className="info-value" title={q.past_injuries || ""}>{clampText(q.past_injuries, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Pain notes:</span>
              <span className="info-value" title={q.pain_notes || ""}>{clampText(q.pain_notes, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Active hobbies:</span>
              <span className="info-value" title={q.active_hobbies || ""}>{clampText(q.active_hobbies, 220)}</span>
            </div>

            <div className="divider"></div>

            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Eating habits:</span>
              <span className="info-value" title={q.eating_habits || ""}>{clampText(q.eating_habits, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Foods you love:</span>
              <span className="info-value" title={q.foods_love || ""}>{clampText(q.foods_love, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Foods you hate/allergies:</span>
              <span className="info-value" title={q.foods_hate_allergies || ""}>{clampText(q.foods_hate_allergies, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Fruits/veggies:</span>
              <span className="info-value" title={q.fruits_veggies || ""}>{clampText(q.fruits_veggies, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Supplements:</span>
              <span className="info-value" title={q.supplements || ""}>{clampText(q.supplements, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Yo-yo diet:</span>
              <span className="info-value" title={q.yo_yo_diet || ""}>{clampText(q.yo_yo_diet, 220)}</span>
            </div>

            <div className="divider"></div>

            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Dietary restrictions:</span>
              <span className="info-value" title={q.dietary_restrictions || ""}>{clampText(q.dietary_restrictions, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Allergies:</span>
              <span className="info-value" title={q.allergies || ""}>{clampText(q.allergies, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Dislikes:</span>
              <span className="info-value" title={q.dislikes || ""}>{clampText(q.dislikes, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Medical constraints:</span>
              <span className="info-value" title={q.medical_constraints || ""}>{clampText(q.medical_constraints, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Injury constraints:</span>
              <span className="info-value" title={q.injury_constraints || ""}>{clampText(q.injury_constraints, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Equipment available:</span>
              <span className="info-value" title={q.equipment_available || ""}>{clampText(q.equipment_available, 220)}</span>
            </div>
            <div className="info-row" style={{ alignItems: "flex-start" }}>
              <span className="info-label">Forbidden topics:</span>
              <span className="info-value" title={q.forbidden_topics || ""}>{clampText(q.forbidden_topics, 220)}</span>
            </div>
          </div>
        </details>
      </div>

      <div style={{ display: "grid", gap: "10px", marginTop: "24px" }}>
        <button onClick={onEditProfile} className="btn-secondary">
          Edit Profile
        </button>

        <button
          onClick={toggleTheme}
          className="btn-secondary"
          type="button"
          disabled={themeSaving}
          title={!canSaveTheme ? "Log in to save theme preference" : ""}
        >
          {themeSaving ? "Saving…" : currentTheme === "dark" ? "🌞 Switch to Light Mode" : "🌙 Switch to Dark Mode"}
        </button>

        <button
          onClick={() => setShowCoachRules(true)}
          className="btn-primary"
          type="button"
          disabled={!token || !authUser?.user_id}
          title={!token ? "Log in again to edit coach rules" : ""}
        >
          🤖 Coach Rules
        </button>
      </div>

      <CoachRulesModal
        isOpen={showCoachRules}
        onClose={() => setShowCoachRules(false)}
        token={token}
        userId={authUser?.user_id || ""}
        titleName={authUser?.name || profile?.name || "Me"}
        readOnly={false}
        onSaved={() => refreshCoachRulesEmpty()}
        showToast={showToast}
      />
    </div>
  );
}
